import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { faTag } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useIdentifyPackage } from '../../hooks/useIdentifyPackage';
import { AdvancedEntrySelect } from '../ui/AdvancedEntrySelect';
import { DestinationType, DESTINATION_TYPE_OPTIONS, IdentifyPayload, Package, Reception } from '../../models/Reception';

interface IdentifyPackageModalProps {
  reception: Reception;
  pkg: Package;
}

const IdentifyPackageModal = forwardRef<ModalCenterRef, IdentifyPackageModalProps>(({ reception, pkg }, ref) => {
  const { t } = useTranslation('receptionsModule');
  const { toast } = useToast();
  const identifyMutation = useIdentifyPackage();

  // Estado del formulario
  const [destinationType, setDestinationType] = useState<DestinationType | ''>('');
  const [destinationRef, setDestinationRef] = useState<string>('');

  // Campos del payload según tipo de destino
  const [selectedEntryId, setSelectedEntryId] = useState<string>('');
  const [totalOrders, setTotalOrders] = useState<number | ''>('');
  const [consumable, setConsumable] = useState('');

  // Reset del formulario
  const resetForm = () => {
    setDestinationType('');
    setDestinationRef('');
    setSelectedEntryId('');
    setTotalOrders('');
    setConsumable('');
  };

  // Resetear campos de payload cuando cambia el tipo de destino
  useEffect(() => {
    setSelectedEntryId('');
    setTotalOrders('');
    setConsumable('');
  }, [destinationType]);

  const handleCancel = () => {
    (ref as React.RefObject<ModalCenterRef>)?.current?.closeModal();
    resetForm();
  };

  const buildPayload = (): IdentifyPayload | null => {
    switch (destinationType) {
      case 'INBOUND':
        if (!selectedEntryId) return null;
        return { entry_id: Number(selectedEntryId) };
      case 'REJECTED':
      case 'RETURN':
        if (totalOrders === '' || totalOrders < 0) return null;
        return { total_orders: Number(totalOrders) };
      case 'SUPPLY':
        if (!consumable.trim()) return null;
        return { consumable: consumable.trim() };
      default:
        return null;
    }
  };

  const handleIdentify = async () => {
    try {
      if (!destinationType) {
        toast({
          title: t('errors.generic'),
          description: t('errors.required_field'),
          variant: 'destructive',
        });
        return;
      }

      const payload = buildPayload();
      if (!payload) {
        toast({
          title: t('errors.generic'),
          description: t('errors.required_field'),
          variant: 'destructive',
        });
        return;
      }

      await identifyMutation.mutateAsync({
        receptionId: reception.id,
        pkg,
        receptionStatus: reception.status,
        data: {
          destination_type: destinationType as DestinationType,
          destination_ref: destinationRef ? parseInt(destinationRef) : null,
          payload,
        },
      });

      toast({
        title: t('modal.identify.success_title'),
        description: t('modal.identify.success_description'),
      });

      resetForm();
      handleCancel();
    } catch (error: any) {
      toast({
        title: t('modal.identify.error_title'),
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  const isLoading = identifyMutation.isPending;

  const getPackageTypeLabel = () => {
    if (pkg.package_type === 'OTHER' && pkg.package_type_other) {
      return pkg.package_type_other;
    }
    return t(`package_types.${pkg.package_type.toLowerCase()}`);
  };

  return (
    <ModalCenter
      ref={ref}
      title={t('modal.identify.title')}
      type="default"
      width="lg"
      classNameBody="max-h-[85vh] flex flex-col"
    >
      <div className="flex-1 overflow-y-auto pr-2">
        <div className="space-y-6">
          {/* Info del paquete */}
          <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-lg">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <FontAwesomeIcon icon={faTag} className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                {t('modal.identify.subtitle', { id: pkg.id, type: getPackageTypeLabel() })}
              </p>
              <p className="text-xs text-muted-foreground">Cantidad: {pkg.quantity} unidades</p>
            </div>
          </div>

          {/* Tipo de destino */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">{t('modal.identify.destination_type_label')}</label>
            <Select
              value={destinationType}
              onValueChange={(value) => setDestinationType(value as DestinationType)}
              disabled={isLoading}
            >
              <SelectTrigger data-testid="destination-type-select">
                <SelectValue placeholder={t('modal.identify.destination_type_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {DESTINATION_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Referencia de destino (opcional) */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">{t('modal.identify.destination_ref_label')}</label>
            <Input
              type="number"
              placeholder={t('modal.identify.destination_ref_placeholder')}
              value={destinationRef}
              onChange={(e) => setDestinationRef(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {/* Campos de payload dinámicos según tipo de destino */}
          {destinationType && (
            <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
              <h4 className="text-sm font-medium text-foreground">{t('modal.identify.payload_title')}</h4>

              {destinationType === 'INBOUND' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">{t('modal.identify.entry_label')}</label>
                  <AdvancedEntrySelect
                    value={selectedEntryId}
                    onValueChange={setSelectedEntryId}
                    placeholder={t('modal.identify.entry_placeholder')}
                    disabled={isLoading}
                    dataTestId="entry-select"
                  />
                </div>
              )}

              {(destinationType === 'REJECTED' || destinationType === 'RETURN') && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    {t('modal.identify.total_orders_label')}
                  </label>
                  <Input
                    type="number"
                    min={0}
                    placeholder={t('modal.identify.total_orders_placeholder')}
                    value={totalOrders}
                    onChange={(e) => setTotalOrders(e.target.value ? parseInt(e.target.value) : '')}
                    disabled={isLoading}
                    data-testid="total-orders-input"
                  />
                </div>
              )}

              {destinationType === 'SUPPLY' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">{t('modal.identify.consumable_label')}</label>
                  <Input
                    placeholder={t('modal.identify.consumable_placeholder')}
                    value={consumable}
                    onChange={(e) => setConsumable(e.target.value)}
                    disabled={isLoading}
                    data-testid="consumable-input"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer fijo siempre visible */}
      <div className="flex-shrink-0 pt-4 mt-4 border-t border-border">
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleCancel} size="lg" disabled={isLoading}>
            {t('button.cancel')}
          </Button>
          <Button variant="primary" onClick={handleIdentify} size="lg" disabled={isLoading || !destinationType}>
            {isLoading ? t('button.identifying') : t('button.identify')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

IdentifyPackageModal.displayName = 'IdentifyPackageModal';

export default IdentifyPackageModal;
